class LyckdReviewsCarousel extends HTMLElement {
  static AUTO_ADVANCE_DELAY = 6500;
  static MANUAL_IDLE_DELAY = 18000;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.slides = Array.from(this.querySelectorAll('[data-review-slide]'));
    this.previousButton = this.querySelector('[data-reviews-previous]');
    this.nextButton = this.querySelector('[data-reviews-next]');
    this.activeIndex = 0;
    this.isAnimating = false;
    this.isPointerInside = false;
    this.isFocusInside = false;
    this.isManualPause = false;
    this.events = new AbortController();

    if (!this.slides.length || !this.previousButton || !this.nextButton) return;

    const hasMultipleReviews = this.slides.length > 1;
    this.previousButton.disabled = !hasMultipleReviews;
    this.nextButton.disabled = !hasMultipleReviews;
    const eventOptions = { signal: this.events.signal };
    this.previousButton.addEventListener('click', () => this.navigateManually(-1), eventOptions);
    this.nextButton.addEventListener('click', () => this.navigateManually(1), eventOptions);
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.navigateManually(event.key === 'ArrowLeft' ? -1 : 1);
    }, eventOptions);
    [this.previousButton, this.nextButton].forEach((button) => {
      button.addEventListener('mouseenter', () => {
        this.isPointerInside = true;
        this.stopAutoAdvance();
      }, eventOptions);
      button.addEventListener('mouseleave', () => {
        this.isPointerInside = false;
        this.scheduleAutoAdvance();
      }, eventOptions);
    });
    this.addEventListener('focusin', () => {
      this.isFocusInside = true;
      this.stopAutoAdvance();
    }, eventOptions);
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.isFocusInside = false;
      this.scheduleAutoAdvance();
    }, eventOptions);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAutoAdvance();
      else this.scheduleAutoAdvance();
    }, eventOptions);

    this.scheduleAutoAdvance();
  }

  disconnectedCallback() {
    this.stopAutoAdvance();
    window.clearTimeout(this.manualResumeTimer);
    this.events?.abort();
    this.initialized = false;
  }

  navigateManually(direction) {
    this.stopAutoAdvance();
    window.clearTimeout(this.manualResumeTimer);
    this.isManualPause = true;
    this.show(this.activeIndex + direction, direction);
    this.manualResumeTimer = window.setTimeout(() => {
      this.isManualPause = false;
      this.scheduleAutoAdvance();
    }, LyckdReviewsCarousel.MANUAL_IDLE_DELAY);
  }

  scheduleAutoAdvance() {
    this.stopAutoAdvance();
    if (
      this.slides?.length < 2 ||
      this.isManualPause ||
      this.isPointerInside ||
      this.isFocusInside ||
      document.hidden ||
      this.prefersReducedMotion()
    ) return;

    this.autoAdvanceTimer = window.setTimeout(() => {
      this.show(this.activeIndex + 1, 1);
      this.scheduleAutoAdvance();
    }, LyckdReviewsCarousel.AUTO_ADVANCE_DELAY);
  }

  stopAutoAdvance() {
    window.clearTimeout(this.autoAdvanceTimer);
    this.autoAdvanceTimer = undefined;
  }

  show(index, direction) {
    if (this.isAnimating || this.slides.length < 2) return;

    const nextIndex = (index + this.slides.length) % this.slides.length;
    if (nextIndex === this.activeIndex) return;

    const currentSlide = this.slides[this.activeIndex];
    const nextSlide = this.slides[nextIndex];
    const enteringClass = direction > 0 ? 'is-entering-next' : 'is-entering-previous';
    const leavingClass = direction > 0 ? 'is-leaving-next' : 'is-leaving-previous';

    this.isAnimating = true;
    nextSlide.hidden = false;
    nextSlide.classList.add(enteringClass);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        currentSlide.classList.remove('is-active');
        currentSlide.classList.add(leavingClass);
        nextSlide.classList.add('is-active');
      });
    });

    window.setTimeout(() => {
      currentSlide.hidden = true;
      currentSlide.classList.remove(leavingClass);
      nextSlide.classList.remove(enteringClass);
      this.activeIndex = nextIndex;
      this.isAnimating = false;
    }, this.prefersReducedMotion() ? 20 : 380);
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('lyckd-reviews-carousel')) {
  customElements.define('lyckd-reviews-carousel', LyckdReviewsCarousel);
}
